import moment, { Moment } from 'moment'

function formatDate(date: string) {
  if (!date) {
    return null
  }
  return date?.substring(0, 10)?.split('-')?.reverse()?.join('/')
}

function stringToDate(date: string) {
  if (!date) return
  const [year, month, day] = date.split('-')
  return new Date(Number(year), Number(month) - 1, Number(day))
}

const formatMomentDate = (date: Moment) => {
  return moment(date).format('YYYY-MM-DD')
}

type DateFormatParams = {
  options?: Intl.DateTimeFormatOptions
  date: Date | null
}

const dateFormat = ({
  options = { month: 'long', year: 'numeric' },
  date
}: DateFormatParams) => {
  if (!date) return ''
  return Intl.DateTimeFormat('pt-BR', options).format(moment(date).toDate())
}

/**
 * Formats a date range into a friendly label or DD/MM/YYYY format
 * @param startDateStr - Start date in YYYY-MM-DD format
 * @param endDateStr - End date in YYYY-MM-DD format
 * @returns Friendly label (e.g., "Este mês", "Últimos 7 dias") or "DD/MM/YYYY - DD/MM/YYYY"
 */
const formatDateRangeLabel = (
  startDateStr: string,
  endDateStr: string
): string => {
  const formatDatePtBr = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00')
    return date.toLocaleDateString('pt-BR')
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const startDate = new Date(startDateStr + 'T00:00:00')
  const endDate = new Date(endDateStr + 'T00:00:00')

  // Helper to get start of month
  const getStartOfMonth = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), 1)

  // Helper to get end of month
  const getEndOfMonth = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth() + 1, 0)

  // Compare dates (ignoring time)
  const isSameDay = (d1: Date, d2: Date) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()

  // Today
  if (isSameDay(startDate, today) && isSameDay(endDate, today)) {
    return 'Hoje'
  }

  // Yesterday
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  if (isSameDay(startDate, yesterday) && isSameDay(endDate, yesterday)) {
    return 'Ontem'
  }

  // Last 7 days (moment().subtract(7, 'days') to today)
  const last7DaysStart = new Date(today)
  last7DaysStart.setDate(today.getDate() - 7)
  if (isSameDay(startDate, last7DaysStart) && isSameDay(endDate, today)) {
    return 'Últimos 7 dias'
  }

  // Last 30 days (moment().subtract(30, 'days') to today)
  const last30DaysStart = new Date(today)
  last30DaysStart.setDate(today.getDate() - 30)
  if (isSameDay(startDate, last30DaysStart) && isSameDay(endDate, today)) {
    return 'Últimos 30 dias'
  }

  // Current month (start of month to today OR start to end of month)
  const currentMonthStart = getStartOfMonth(today)
  const currentMonthEnd = getEndOfMonth(today)
  if (
    isSameDay(startDate, currentMonthStart) &&
    (isSameDay(endDate, today) || isSameDay(endDate, currentMonthEnd))
  ) {
    return 'Este mês'
  }

  // Last month (full month)
  const lastMonthStart = getStartOfMonth(
    new Date(today.getFullYear(), today.getMonth() - 1, 1)
  )
  const lastMonthEnd = getEndOfMonth(
    new Date(today.getFullYear(), today.getMonth() - 1, 1)
  )
  if (
    isSameDay(startDate, lastMonthStart) &&
    isSameDay(endDate, lastMonthEnd)
  ) {
    return 'Mês passado'
  }

  // Last 3 months (moment().subtract(3, 'months') to today)
  const last3MonthsStart = new Date(today)
  last3MonthsStart.setMonth(today.getMonth() - 3)
  if (isSameDay(startDate, last3MonthsStart) && isSameDay(endDate, today)) {
    return 'Últimos 3 meses'
  }

  // Last 12 months (moment().subtract(12, 'months') to today)
  const last12MonthsStart = new Date(today)
  last12MonthsStart.setMonth(today.getMonth() - 12)
  if (isSameDay(startDate, last12MonthsStart) && isSameDay(endDate, today)) {
    return 'Últimos 12 meses'
  }

  // Single day
  if (isSameDay(startDate, endDate)) {
    return formatDatePtBr(startDateStr)
  }

  // Full specific month (e.g., "Outubro de 2025")
  const startOfSelectedMonth = getStartOfMonth(startDate)
  const endOfSelectedMonth = getEndOfMonth(startDate)
  if (
    isSameDay(startDate, startOfSelectedMonth) &&
    isSameDay(endDate, endOfSelectedMonth)
  ) {
    const monthName = startDate.toLocaleDateString('pt-BR', { month: 'long' })
    const year = startDate.getFullYear()
    // Capitalize first letter
    const capitalizedMonth =
      monthName.charAt(0).toUpperCase() + monthName.slice(1)
    return `${capitalizedMonth} de ${year}`
  }

  // Default: show date range in DD/MM/YYYY format
  return `${formatDatePtBr(startDateStr)} - ${formatDatePtBr(endDateStr)}`
}

export {
  formatDate,
  stringToDate,
  formatMomentDate,
  dateFormat,
  formatDateRangeLabel
}
